import { Alert, Button, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../contexts/SnackbarContext';
import AuthService from '../../services/AuthService';
import { log } from '../../utils/errors/logger';
import type { AuthUser } from '../../types/common';

interface EmailVerificationBannerProps {
  user: AuthUser | null;
}

export const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const [resendingVerification, setResendingVerification] = useState(false);

  const handleResendVerification = async () => {
    if (!user?.email) {
      log.info('No email address found, redirecting to resend verification page');
      navigate('/resend-verification');
      return;
    }

    setResendingVerification(true);

    try {
      log.info('Sending verification email to:', { email: user.email });
      await AuthService.resendVerification(user.email);
      showSuccess(t('verifyEmail.resendSuccess', 'Verifizierungs-E-Mail wurde erneut gesendet.'));
    } catch (error) {
      log.error('Error resending verification:', error);
      showError(t('verifyEmail.resendError', 'Fehler beim Senden der Verifizierungs-E-Mail.'));
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <>
      {/* E-Mail-Verifizierungsstatus Banner */}
      {user && user.email_verified === false && (
        <Alert 
          severity="warning" 
          sx={{ 
            borderRadius: 0, 
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {t('verifyEmail.notVerified', 'Ihre E-Mail-Adresse wurde noch nicht verifiziert.')} 
          <Button 
            color="inherit" 
            size="small" 
            sx={{ ml: 2 }}
            disabled={resendingVerification}
            onClick={handleResendVerification}
          >
            {resendingVerification ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              t('login.resendVerification', 'Verifizierungs-E-Mail erneut senden')
            )}
          </Button>
        </Alert>
      )}
      
      {/* Admin-Freigabe erforderlich Banner */}
      {user && user.email_verified === true && user.needs_admin_approval === true && (
        <Alert 
          severity="info" 
          sx={{ 
            borderRadius: 0, 
            justifyContent: 'center'
          }}
        >
          {t('login.error.awaitingApproval', 'Ihr Konto wurde verifiziert, muss aber noch von einem Administrator freigeschaltet werden.')}
        </Alert>
      )}
    </>
  );
};